/**
 * AdminSalesRegionBars — "Revenue by Region" card. Each row: region name
 * + code, a revenue bar scaled to the top region, the dollar figure, the
 * trailing-6wk sold/cap, and the WoW delta (green up / warn down). Rows
 * arrive pre-sorted by revenue desc from the DTO.
 */
import type { AdminSalesDto } from './adminApi';

const EM_DASH = '—';

/** Two-letter region code from a slug (new-england → NE). */
function regionCode(slug: string): string {
  const initials = slug
    .split('-')
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
  if (initials.length >= 2) return initials.slice(0, 2);
  return slug.slice(0, 2).toUpperCase();
}

function deltaLabel(deltaPct: number | null): { text: string; negative: boolean } {
  if (deltaPct === null) return { text: EM_DASH, negative: false };
  const negative = deltaPct < 0;
  return { text: `${negative ? '−' : '+'}${Math.abs(deltaPct)}%`, negative };
}

function RegionRow({
  row,
  maxRevenue,
}: {
  row: AdminSalesDto['revenueByRegion'][number];
  maxRevenue: number;
}) {
  const dollars = Math.round(row.revenueCents / 100);
  const width = maxRevenue > 0 ? (row.revenueCents / maxRevenue) * 100 : 0;
  const delta = deltaLabel(row.deltaPct);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ width: 140, flex: '0 0 140px' }}>
        <div style={{ fontSize: 12.5, fontWeight: 700 }}>{row.region.name}</div>
        <div className="nb-mono" style={{ fontSize: 10.5, color: 'var(--nb-subtle)' }}>
          {regionCode(row.region.slug)}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="nb-bar" style={{ width: '100%', height: 8 }}>
          <i style={{ width: `${width}%` }} />
        </div>
      </div>
      <div
        className="nb-tabular nb-mono"
        style={{
          width: 90,
          textAlign: 'right',
          fontSize: 13,
          fontWeight: 700,
          color: 'var(--nb-ink)',
        }}
      >
        ${dollars.toLocaleString('en-US')}
      </div>
      <div
        className="nb-tabular"
        style={{ width: 60, textAlign: 'right', fontSize: 11, color: 'var(--nb-subtle)' }}
      >
        {row.ticketsSold}/{row.ticketCap}
      </div>
      <div
        style={{
          width: 56,
          textAlign: 'right',
          fontSize: 11,
          fontWeight: 600,
          color: delta.negative ? 'var(--nb-warn)' : 'var(--nb-primary)',
        }}
      >
        {delta.text}
      </div>
    </div>
  );
}

export function AdminSalesRegionBars({ rows }: { rows: AdminSalesDto['revenueByRegion'] }) {
  const maxRevenue = Math.max(0, ...rows.map((r) => r.revenueCents));
  return (
    <section
      className="nb-card"
      style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      <div className="nb-card-hd">
        <h3 className="nb-display" style={{ fontSize: 13, letterSpacing: '.04em', margin: 0 }}>
          Revenue by Region
        </h3>
        <span className="nb-eyebrow">trailing 6 wks</span>
      </div>
      {rows.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--nb-subtle)' }} role="note">
          Revenue by region unavailable.
        </div>
      ) : (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {rows.map((r) => (
            <RegionRow key={r.region.id} row={r} maxRevenue={maxRevenue} />
          ))}
        </div>
      )}
    </section>
  );
}
